import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Space,
  Button,
  Row,
  Col,
  Divider,
  Spin,
  Alert,
  Tag,
  Progress,
  Layout,
} from "antd";
import { toast } from "react-toastify";
import {
  ArrowLeftOutlined,
  LeftOutlined,
  RightOutlined,
  FileTextOutlined,
  PlayCircleOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileZipOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { getPageData, getPageFile, getAssessmentById, fetchModuleWithDetails } from "../Services/CourseListService";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/TextLayer.css";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const { Title, Text } = Typography;
const { Content } = Layout;

const PageDetails = () => {
  const navigate = useNavigate();
  const { courseId, moduleId, pageId } = useParams();
  const currentPageId = parseInt(pageId) || 1;
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState(null);
  const [currentPageIdState, setCurrentPageIdState] = useState(currentPageId);
  const [assessmentData, setAssessmentData] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [showAssessment, setShowAssessment] = useState(false);
  const [moduleDetails, setModuleDetails] = useState(null);
  const [fileError, setFileError] = useState(null);

  // PDF viewer states
  const [pdfPageNumber, setPdfPageNumber] = useState(1);
  const [pdfTotalPages, setPdfTotalPages] = useState(1);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Load page data
  useEffect(() => {
    loadPageData(currentPageIdState);
  }, [currentPageIdState]);

  // Load module details to get assessment ID
  useEffect(() => {
    if (moduleId) {
      loadModuleDetails();
    }
  }, [moduleId]);

  const loadModuleDetails = async () => {
    try {
      const details = await fetchModuleWithDetails(moduleId);
      setModuleDetails(details);
      console.log(" Module details loaded:", details);
    } catch (error) {
      console.error(" Failed to load module details:", error);
      toast.error(error?.response?.data?.message);
    }
  };

  const loadPageData = async (pageId) => {
    try {
      setLoading(true);
      setFileError(null);
      console.log(` Loading page ID ${pageId} for module ${moduleId}`);

      const data = await getPageData(moduleId, pageId, false);
      console.log(" Full page data response:", data);
      setPageData(data);

      // Only try to load page file if pageFileId exists and is valid
      if (data.currentPage?.pageFileId) {
        console.log(" Trying to load page file with pageFileId:", data.currentPage.pageFileId);
        await loadPageFile(data.currentPage.pageFileId);
      } else {
        console.log("ℹ No pageFileId available for this page");
        setFileContent(null);
        setFileType(null);
      }

      // Check if this is an assessment page
      if (data.isAssessmentPage && data.assessmentPage) {
        await loadAssessmentData(data.assessmentPage.assessmentId);
      } else {
        setAssessmentData(null);
      }

    } catch (error) {
      console.error(" Failed to load page data:", error);
      toast.error(error?.response?.data.message || "Failed to load page content");
    } finally {
      setLoading(false);
    }
  };

  const loadPageFile = async (pageFileId) => {
    try {
      if (!pageFileId || pageFileId <= 0) {
        console.warn("⚠️ Invalid pageFileId:", pageFileId);
        setFileError("No file available for this page");
        setFileContent(null);
        setFileType(null);
        return;
      }

      console.log(" Loading page file for ID:", pageFileId);
      let fileBlob = await getPageFile(pageFileId);

      if (!fileBlob || fileBlob.size === 0) {
        console.warn("⚠️ Empty file blob received");
        setFileError("File is empty or unavailable");
        setFileContent(null);
        setFileType(null);
        return;
      }

      // Check if the blob is actually an error message (small files might be errors)
      if (fileBlob.size < 500 && fileBlob.type.includes('text')) {
        const textContent = await fileBlob.text();
        if (textContent.includes('error') || textContent.includes('Exception') || textContent.includes('500')) {
          console.error(' Server returned error disguised as file:', textContent);
          setFileError("Server error: File content unavailable");
          setFileContent(null);
          setFileType(null);
          return;
        }
        // If it's not an error, recreate the blob
        fileBlob = new Blob([textContent], { type: fileBlob.type });
      }

      // Detect file type from blob
      const fileUrl = URL.createObjectURL(fileBlob);
      const fileType = detectFileType(fileBlob);

      setFileContent(fileUrl);
      setFileType(fileType);
      setFileError(null);

      // Reset PDF viewer state when new file is loaded
      if (fileType === 'pdf') {
        setPdfPageNumber(1);
        setPdfUrl(fileUrl);
        setPdfTotalPages(1);
      }

      console.log(" Page file loaded successfully, type:", fileType, "size:", fileBlob.size, "bytes");

    } catch (error) {
      console.error(" Failed to load page file:", error);
      setFileContent(null);
      setFileType(null);
      setPdfUrl(null);

      // Handle specific error cases
      if (error.response?.status === 500) {
        setFileError("Server error: File temporarily unavailable");
      } else if (error.response?.status === 404) {
        setFileError("File not found on server");
      } else if (error.response?.status === 403) {
        setFileError("Access denied to file");
      } else if (error.message.includes('timeout')) {
        setFileError("File load timeout - please try again");
      } else {
        setFileError(error.message || "Failed to load file content");
      }

      toast.error(`File load failed: ${error.message}`);
    }
  };

  const detectFileType = (blob) => {
    if (!blob || !blob.type) return 'unknown';

    const type = blob.type.toLowerCase();

    if (type.includes('pdf')) return 'pdf';
    if (type.includes('image')) return 'image';
    if (type.includes('text') || type.includes('html')) return 'text';
    if (type.includes('word') || type.includes('document')) return 'document';
    if (type.includes('excel') || type.includes('sheet')) return 'spreadsheet';
    if (type.includes('zip') || type.includes('compressed')) return 'archive';

    return 'unknown';
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
      case 'image': return <FileImageOutlined style={{ color: '#52c41a' }} />;
      case 'document': return <FileWordOutlined style={{ color: '#1890ff' }} />;
      case 'spreadsheet': return <FileExcelOutlined style={{ color: '#52c41a' }} />;
      case 'archive': return <FileZipOutlined style={{ color: '#faad14' }} />;
      default: return <FileTextOutlined style={{ color: '#722ed1' }} />;
    }
  };

  const getFileTypeName = (type) => {
    switch (type) {
      case 'pdf': return 'PDF Document';
      case 'image': return 'Image File';
      case 'text': return 'Text File';
      case 'document': return 'Word Document';
      case 'spreadsheet': return 'Excel Spreadsheet';
      case 'archive': return 'Compressed Archive';
      default: return 'File';
    }
  };

  // PDF navigation functions
  const nextPdfPage = () => {
    if (pdfPageNumber < pdfTotalPages) {
      setPdfPageNumber(pdfPageNumber + 1);
    }
  };

  const prevPdfPage = () => {
    if (pdfPageNumber > 1) {
      setPdfPageNumber(pdfPageNumber - 1);
    }
  };

  const onPdfLoadSuccess = ({ numPages }) => {
    setPdfTotalPages(numPages);
    setPdfLoading(false);
  };

  const onPdfLoadError = (error) => {
    console.error(" PDF load error:", error);
    toast.error(error?.response?.data?.message)
    setPdfLoading(false);
    setFileError("Failed to load PDF document");
  };

  const onPdfLoadStart = () => {
    setPdfLoading(true);
  };

  const renderFileContent = () => {
    if (fileError) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <ExclamationCircleOutlined style={{ fontSize: '48px', color: '#faad14', marginBottom: '16px' }} />
          <Title level={4} type="warning">File Unavailable</Title>
          <Text type="secondary">{fileError}</Text>
          <div style={{ marginTop: '16px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Page File ID: {pageData?.currentPage?.pageFileId}
            </Text>
          </div>
        </div>
      );
    }

    if (!fileContent || !fileType) return null;

    switch (fileType) {
      case 'pdf':
        return (
          <div style={{ width: '100%' }}>
            {/* PDF Viewer with Page Navigation */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "20px",
                backgroundColor: "#f5f7fa",
                borderRadius: "8px",
                minHeight: "600px"
              }}
            >
              <div
                style={{
                  backgroundColor: "#fff",
                  border: "1px solid #e0e0e0",
                  maxWidth: "900px",
                  width: "100%",
                  overflow: "hidden",
                  marginBottom: "20px"
                }}
              >
                {pdfUrl && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      overflowX: "auto",
                      padding: "10px",
                    }}
                  >
                    <Document
                      file={pdfUrl}
                      onLoadSuccess={onPdfLoadSuccess}
                      onLoadError={onPdfLoadError}
                      onLoadStart={onPdfLoadStart}
                      loading={
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                          <Spin size="large" />
                          <div style={{ marginTop: '16px' }}>
                            <Text>Loading PDF document...</Text>
                          </div>
                        </div>
                      }
                    >
                      <Page
                        pageNumber={pdfPageNumber}
                        width={800}
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                        loading={
                          <div style={{ textAlign: 'center', padding: '40px' }}>
                            <Spin size="large" />
                            <div style={{ marginTop: '16px' }}>
                              <Text>Loading page {pdfPageNumber}...</Text>
                            </div>
                          </div>
                        }
                      />
                    </Document>
                  </div>
                )}
              </div>


            </div>
          </div>
        );

      case 'image':
        return (
          <div style={{ textAlign: 'center' }}>
            <img
              src={fileContent}
              alt="Page content"
              style={{
                maxWidth: '100%',
                maxHeight: '500px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
              onError={(e) => {
                console.error(" Image load error");
                toast.error(e?.response?.data?.message);
                setFileError("Failed to load image");
              }}
            />
            <div style={{ marginTop: '16px' }}>
              <Button
                type="link"
                href={fileContent}
                download={`page-${currentPageIdState}.${fileType}`}
                icon={<FileTextOutlined />}
              >
                Download Image
              </Button>
            </div>
          </div>
        );

      case 'text':
        return (
          <div style={{ textAlign: 'center' }}>
            <Button
              type="primary"
              href={fileContent}
              target="_blank"
              icon={<FileTextOutlined />}
            >
              View Text File
            </Button>
            <div style={{ marginTop: '16px' }}>
              <Button
                type="link"
                href={fileContent}
                download={`page-${currentPageIdState}.txt`}
                icon={<FileTextOutlined />}
              >
                Download Text File
              </Button>
            </div>
          </div>
        );

      default:
        return (
          <div style={{ textAlign: 'center' }}>
            <Space direction="vertical" size="middle">
              {getFileIcon(fileType)}
              <Title level={4}>{getFileTypeName(fileType)}</Title>
              <Text>This file type is best viewed by downloading</Text>
              <div style={{ marginTop: '16px' }}>
                <Button
                  type="primary"
                  href={fileContent}
                  target="_blank"
                  download={`page-${currentPageIdState}`}
                  icon={<FileTextOutlined />}
                >
                  Download File
                </Button>
              </div>
            </Space>
          </div>
        );
    }
  };

  const loadAssessmentData = async (assessmentId) => {
    try {
      console.log(" Loading assessment data for ID:", assessmentId);
      const data = await getAssessmentById(assessmentId);
      setAssessmentData(data);
      console.log(" Assessment data loaded:", data);
    } catch (error) {
      console.error(" Failed to load assessment data:", error);
      toast.error(error?.response?.data?.message);
    }
  };

  const loadModuleAssessment = async () => {
    try {
      if (moduleDetails?.assessment?.id) {
        const assessmentId = moduleDetails.assessment.id;
        console.log(" Loading module assessment with ID:", assessmentId);
        const data = await getAssessmentById(assessmentId);
        setAssessmentData(data);
        console.log(" Module assessment data loaded:", data);
        return true;
      }
      return false;
    } catch (error) {
      console.error(" Failed to load module assessment:", error);
      toast.error(error?.response?.data?.message);
      return false;
    }
  };

  const navigateToPage = (pageId) => {
    if (pageId >= 1) {
      setCurrentPageIdState(pageId);
      setShowAssessment(false);
      window.history.pushState(null, '', `/proAdmin-dashboard/courses/${courseId}/modules/${moduleId}/pages/${pageId}`);
    }
  };

  const navigateToAssessment = async () => {
    try {
      setLoading(true);
      console.log(" Navigating to assessment...");

      const success = await loadModuleAssessment();
      if (success) {
        setShowAssessment(true);
        window.history.pushState(null, '', `/proAdmin-dashboard/courses/${courseId}/modules/${moduleId}/assessment`);
        console.log(" Successfully navigated to assessment");
      } else {
        console.log("⚠️ No assessment available, going back to module");
        toast.warning("No assessment available for this module");
        handleBack();
      }
    } catch (error) {
      console.error(" Failed to navigate to assessment:", error);
      toast.error("Failed to load assessment");
    } finally {
      setLoading(false);
    }
  };



  const handleBack = () => {
    navigate(`/proAdmin-dashboard/courses/${courseId}/modules/${moduleId}`);
  };

  const formatTime = (timeString) => {
    if (!timeString || timeString === "00:00:00") return "Not specified";

    const [hours, minutes] = timeString.split(":");
    const hoursInt = parseInt(hours);
    const minutesInt = parseInt(minutes);

    if (hoursInt === 0 && minutesInt === 0) return "Not specified";
    if (hoursInt === 0) return `${minutesInt} minute${minutesInt !== 1 ? 's' : ''}`;
    if (minutesInt === 0) return `${hoursInt} hour${hoursInt !== 1 ? 's' : ''}`;

    return `${hoursInt}h ${minutesInt}m`;
  };

  const getProgressPercentage = () => {
    if (!pageData?.totalPages) return 0;

    if (showAssessment) {
      // When showing assessment, progress is 100%
      return 100;
    }

    const totalItems = pageData.totalPages + (pageData.nextIsAssessment ? 1 : 0);
    const currentItem = pageData.currentPage?.pageNumber || currentPageIdState;
    return Math.round((currentItem / totalItems) * 100);
  };

  const getCurrentDisplayNumber = () => {
    if (showAssessment) {
      return "Assessment";
    }
    return pageData?.currentPage?.pageNumber || currentPageIdState;
  };

  const getTotalDisplayPages = () => {
    const totalPages = pageData?.totalPages || 0;
    return totalPages + (pageData?.nextIsAssessment ? 1 : 0);
  };

  const isLastContentPage = pageData?.nextIsAssessment && !pageData?.hasNext;

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      if (fileContent) {
        URL.revokeObjectURL(fileContent);
      }
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [fileContent, pdfUrl]);

  if (loading && !pageData) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!pageData) {
    return (
      <Alert
        message="Page Not Found"
        description="The requested page could not be found."
        type="error"
        showIcon
        style={{ margin: "20px" }}
        action={
          <Button size="small" onClick={handleBack}>
            Go Back
          </Button>
        }
      />
    );
  }

  const renderAssessmentContent = () => {
    if (!assessmentData) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            <Text>Loading assessment content...</Text>
          </div>
        </div>
      );
    }

    return (
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <Title level={3}>{assessmentData.title}</Title>
        <Text strong>This assessment contains {assessmentData.questions?.length || 0} questions</Text>
        <Text>Estimated Time: {formatTime(assessmentData.estimatedTime)}</Text>
        <Text type="secondary">Passing Score: {assessmentData.passingScore || 70}%</Text>

        {assessmentData.questions?.map((question, index) => (
          <Card
            key={question.id}
            style={{ marginBottom: '16px', border: '1px solid #d9d9d9' }}
            title={`Question ${index + 1}`}
          >
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              <Text strong>{question.questionText}</Text>
              <Space direction="vertical" style={{ width: "100%" }}>
                {question.options?.map((option) => (
                  <div key={option.id} style={{
                    padding: '8px 12px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    background: option.isCorrect ? '#f6ffed' : 'white',
                    borderColor: option.isCorrect ? '#b7eb8f' : '#d9d9d9'
                  }}>
                    <Text>{option.text}</Text>
                    {option.isCorrect && (
                      <Tag color="green" style={{ marginLeft: '8px' }}>
                        Correct Answer
                      </Tag>
                    )}
                  </div>
                ))}
              </Space>
            </Space>
          </Card>
        ))}
      </Space>
    );
  };

  const renderContentArea = () => {
    if (showAssessment) {
      return renderAssessmentContent();
    }

    // Your existing content rendering logic
    if (pageData.isAssessmentPage) {
      // Assessment Page Content
      return (
        <div>
          {assessmentData ? (
            <Space direction="vertical" style={{ width: "100%" }} size="large">
              <Title level={3}>{assessmentData.title}</Title>
              <Text strong>This assessment contains {assessmentData.questions?.length || 0} questions</Text>

              {assessmentData.questions?.map((question, index) => (
                <Card
                  key={question.id}
                  style={{ marginBottom: '16px', border: '1px solid #d9d9d9' }}
                  title={`Question ${index + 1}`}
                >
                  <Space direction="vertical" style={{ width: "100%" }} size="middle">
                    <Text strong>{question.questionText}</Text>
                    <Space direction="vertical" style={{ width: "100%" }}>
                      {question.options?.map((option, optIndex) => (
                        <div key={option.id} style={{
                          padding: '8px 12px',
                          border: '1px solid #d9d9d9',
                          borderRadius: '4px',
                          background: option.isCorrect ? '#f6ffed' : 'white',
                          borderColor: option.isCorrect ? '#b7eb8f' : '#d9d9d9'
                        }}>
                          <Text>{option.text}</Text>
                          {option.isCorrect && (
                            <Tag color="green" style={{ marginLeft: '8px' }}>
                              Correct Answer
                            </Tag>
                          )}
                        </div>
                      ))}
                    </Space>
                  </Space>
                </Card>
              ))}
            </Space>
          ) : pageData.assessmentPage ? (
            <Space direction="vertical" style={{ width: "100%" }} size="large">
              <Title level={3}>{pageData.assessmentPage.title}</Title>
              <Text strong>Assessment Page</Text>
              <Text>Estimated Time: {formatTime(pageData.assessmentPage.estimatedTime)}</Text>
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
                <div style={{ marginTop: '16px' }}>
                  <Text>Loading assessment questions...</Text>
                </div>
              </div>
            </Space>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px' }}>
                <Text>Loading assessment content...</Text>
              </div>
            </div>
          )}
        </div>
      );
    } else {
      // Regular Page Content
      return (
        <div>
          {fileContent || fileError ? (
            <div>
              {renderFileContent()}
            </div>
          ) : pageData.currentPage?.htmlContent ? (
            <div
              dangerouslySetInnerHTML={{ __html: pageData.currentPage.htmlContent }}
              style={{
                maxWidth: '100%',
                lineHeight: '1.6',
                fontSize: '16px'
              }}
            />
          ) : pageData.currentPage?.text ? (
            <Text style={{ fontSize: '16px', lineHeight: '1.6' }}>
              {pageData.currentPage.text}
            </Text>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <FileTextOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
              <Title level={4} type="secondary">No Content Available</Title>
              <Text type="secondary">
                This page exists but has no displayable content.
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px' }}>
                Page ID: {currentPageIdState} | Module ID: {moduleId}
                {pageData.currentPage?.pageFileId && ` | File ID: ${pageData.currentPage.pageFileId}`}
              </Text>
            </div>
          )}
        </div>
      );
    }
  };

  const renderNavigationButtons = () => {
    if (showAssessment) {
      return (
        <>
          <Col>
            <Space>
              <Text type="secondary">
                Assessment
              </Text>
            </Space>
          </Col>

          <Col>
            <Button
              type="primary"
              onClick={handleBack}
              size="large"
              icon={<PlayCircleOutlined />}
            >
              Complete Module
            </Button>
          </Col>
        </>
      );
    }

    return (
      <>
        <Col>
          {pageData.hasPrevious && pageData.previousPageId && (
            <Button
              type="default"
              icon={<LeftOutlined />}
              onClick={() => navigateToPage(pageData.previousPageId)}
              size="large"
            >
              Previous Page
            </Button>
          )}
        </Col>

        <Col>
          <Space>
            <Text type="secondary">
              Page  {getCurrentDisplayNumber()} of {getTotalDisplayPages()}
            </Text>
          </Space>
        </Col>

        <Col>
          {isLastContentPage ? (
            <Button
              type="primary"
              onClick={navigateToAssessment}
              size="large"
              icon={<RightOutlined />}
            >
              Start Assessment
            </Button>
          ) : pageData.hasNext && pageData.nextPageId ? (
            <Button
              type="primary"
              onClick={() => navigateToPage(pageData.nextPageId)}
              size="large"
              icon={<RightOutlined />}
            >
              Next Page
            </Button>
          ) : (
            <Button
              type="primary"
              onClick={handleBack}
              size="large"
              icon={<PlayCircleOutlined />}
            >
              Complete Module
            </Button>
          )}
        </Col>
      </>
    );
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f7fa" }}>
      <Content style={{ padding: "24px" }}>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>

          {/* Header Card */}
          <Card
            style={{
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              border: 'none',
              background: showAssessment
                ? 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)'
                : pageData.isAssessmentPage
                  ? 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
            bodyStyle={{ padding: '24px' }}
          >
            <Row justify="space-between" align="middle">
              <Col>
                <Space direction="vertical" size="small">
                  <Title level={2} style={{ margin: 0, color: 'white' }}>
                    <FileTextOutlined />
                    {showAssessment || pageData.isAssessmentPage ? "Assessment" : `Page  ${getCurrentDisplayNumber()}`}
                  </Title>
                  <Text style={{ color: 'rgba(255,255,255,0.8)' }}>
                    Course ID: {courseId} | Module ID: {moduleId} |
                    {showAssessment || pageData.isAssessmentPage ? " Final Assessment" : ` Page  ${getCurrentDisplayNumber()} of ${getTotalDisplayPages()}`}
                  </Text>
                </Space>
              </Col>
              <Col>
                <Button
                  type="default"
                  icon={<ArrowLeftOutlined />}
                  onClick={handleBack}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    color: 'white'
                  }}
                >
                  Back to Module
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Progress Bar */}
          <Card style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
            <Space direction="vertical" style={{ width: "100%" }} size="small">
              <Row justify="space-between" align="middle">
                <Col>
                  <Text strong>Module Progress</Text>
                </Col>
                <Col>
                  <Text type="secondary">
                    {showAssessment || pageData.isAssessmentPage ? "Assessment" : `Page  ${getCurrentDisplayNumber()}`} of {getTotalDisplayPages()}
                  </Text>
                </Col>
              </Row>
              <Progress
                percent={getProgressPercentage()}
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
                showInfo={false}
              />
            </Space>
          </Card>

          {/* Main Content Window */}
          <Card
            style={{
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
              border: '1px solid #e8e8e8',
              borderRadius: '12px',
              minHeight: '500px',
              background: 'white'
            }}
            bodyStyle={{ padding: '32px' }}
          >
            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
                <Spin size="large" />
              </div>
            ) : (
              <Space direction="vertical" style={{ width: "100%" }} size="large">



                <Divider style={{ margin: '16px 0' }} />

                {/* Content Area */}
                <div style={{
                  minHeight: '400px',
                  padding: '20px',
                  border: '1px solid #f0f0f0',
                  borderRadius: '8px',
                  background: '#fafafa'
                }}>
                  {renderContentArea()}
                </div>

                {/* Navigation Footer */}
                <Divider style={{ margin: '24px 0' }} />

                <Row justify="space-between" align="middle">
                  {renderNavigationButtons()}
                </Row>
              </Space>
            )}
          </Card>
        </Space>
      </Content>
    </Layout>
  );
};

export default PageDetails;